import type {NotificationNode as NotificationNodeType} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2';

import * as React from 'react';
import {useHistory, useParams} from 'src/rerouter';
import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';

import {useChatbotStateContext} from '../chatbot-state-context';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useReleaseFlag} from 'src/hooks/product-flags';

import {useMutableFieldStatus} from '../api-hooks';

import {
  createNotificationNode,
  createEditableGraph,
  findAncestorNodeVariables,
  getHasAncestorJobMatchQuestionNode,
  getIsUsingDiscoverAttribute,
} from '../utils';

import {validateNode} from '../node-editor/validation';

import {Button} from 'src/designSystem2021Components/buttons';

import {
  MessageDraftEditor,
  upgradePossibleEntitiesInRDCS,
} from 'src/components/conversation-builder/draft-editor';

import {Input} from 'src/designSystem2021Components/input';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardActions,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {TokenListInput} from 'src/components/lib/token-list-input';
import {useEditorState, useChatbotVariables} from '../node-editor/reducer';
import css from './node-editor-v2.css';
import {useSelector} from 'react-redux';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';
import RichEditorWithWarning from 'src/components/conversation-builder/lib/rich-editor-with-warning/rich-editor-with-warning';
import {getDiscoverAttributeWarningMessage} from 'src/components/conversation-builder/constants';
import {type RawDraftContentState, type DraftDecoratorType} from 'draft-js';


export default function NotificationNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {t} = useLabelTranslation();
  const richTextEditorEnabled = false; //useReleaseFlag('chatbotFlowRichEditor'); TODO: enable when this is supported for notification node
  const {flowId} = useParams();
  const router = useHistory();
  const richTextEditorFormattingEnabled = useReleaseFlag(
    'chatbotFlowRichEditorFormatting',
  );
  const notificationEnhancementEnabled = useReleaseFlag(
    'chatbotNotificationNodeEnhancement',
  );
  const atsMappings = useSelector(selectEntityTypeMappingsByName);
  const chatbotAdvanceEmailOptionEnabled = useReleaseFlag(
    'chatbotAdvanceEmailOption',
  );
  const allowMeetingNodeVariables = Boolean(
    useReleaseFlag('chatbotMeetingNodeVariable'),
  );
  const chatbotNewHyperlinkModalEnabled = useReleaseFlag(
    'chatbotNewHyperlinkModal',
  );
  const {state, dispatch} = useChatbotStateContext();
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [emailType, setEmailType] = React.useState('basic');

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'notification'
      ? editingNodeData
      : createNotificationNode();

  const edgeId = Number(state.insertingNode?.edgeId || state.editingNode);

  const [editorState, editorDispatch] = useEditorState<NotificationNodeType>({
    defaultNode,
  });

  const {isFieldReadOnly, canFlowUpdate} = useMutableFieldStatus(
    readOnly,
    defaultNode.type,
  );

  const {node} = editorState;

  const setNode = (payload: NotificationNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const editableGraph = createEditableGraph(graph);
  const showFileUploadNodeVar = Boolean(
    useReleaseFlag('chatbotFileUploadLinkNotificationEnabled'),
  );
  const showJobMatchNodeVar = Boolean(
    useReleaseFlag('chatbotCustomJobResults'),
  );
  const createAdvancedEmailBtnDisabled =
    node.name.trim().length === 0 ||
    node['notification_to_recipients']?.length === 0 ||
    node['notification_email_subject']?.blocks[0].text.trim().length === 0;

  const nodeVariables = findAncestorNodeVariables(
    editableGraph,
    defaultNode,
    edgeId,
    showFileUploadNodeVar,
    showJobMatchNodeVar,
    allowMeetingNodeVariables,
  );
  const mappedNodeVariables = nodeVariables.map((nodeVar) => ({
    ...nodeVar,
    key: nodeVar.node_id,
    label: nodeVar.name,
  }));

  const hasAncestorJobMatchQuestionNode = getHasAncestorJobMatchQuestionNode(
    editableGraph,
    defaultNode,
    state.insertingNode?.edgeId,
  );

  const dynamicLabels = useChatbotVariables(graph.anchor_entity_type, {
    showDiscoverAttributes: hasAncestorJobMatchQuestionNode,
    showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
  });

  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const dynamicLabelMap = React.useMemo(() => keyBy(dynamicLabels, 'value'));
  const emailDynamicLabels = React.useMemo(
    () =>
      uniqBy(
        dynamicLabels.filter(
          (label) =>
            label.display_entity_type === 'global_variable' ||
            ['email'].includes(label.type),
        ),
        'label',
      ),
    [dynamicLabels],
  );

  const anscestorQuestions = !isNaN(edgeId)
    ? findAncestorNodeVariables(
        createEditableGraph(graph),
        node,
        Number(edgeId),
        showFileUploadNodeVar,
        showJobMatchNodeVar,
        allowMeetingNodeVariables,
      )
    : [];
  const nodeIdOptions = anscestorQuestions.map(({name, node_id}) => {
    return {
      value: node_id,
      label: `"${name}"`,
    };
  });

  const tokenOptions =
    graph.use_case === 'sourcing'
      ? nodeIdOptions
      : [...nodeIdOptions, ...emailDynamicLabels];

  const validation = validateNode(node, editableGraph);
  const error = hasSubmitted ? validation : null;

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const prepareContentOnLoad = (
    rdcs: RawDraftContentState,
    decorator: DraftDecoratorType,
  ) =>
    upgradePossibleEntitiesInRDCS(
      rdcs,
      editableGraph,
      nodeVariables,
      dynamicLabels,
      decorator,
    );

  const isUsingDiscoverAttribute = getIsUsingDiscoverAttribute(
    node.notification_text_content,
  );

  const isUsingDiscoverAttributeInSubject = getIsUsingDiscoverAttribute(
    node.notification_email_subject,
  );

  const DISCOVER_ATTRIBUTE_WARNING_MESSAGE =
    getDiscoverAttributeWarningMessage(t);

  return (
    <div>
      <div className={css.questionNodeContainer}>
        <div className={css.section}>
          <Input
            label={t('Node_Title', 'Node Title')}
            value={node.name}
            onChange={(event) =>
              setNode({...node, name: event.currentTarget.value})
            }
          />
          {error?.name && (
            <div className={css.errorText}>
              {error.name
                ? error.name.errors?.includes('EMPTY')
                  ? `${t(
                      'This_node_must_have_a_name',
                      'This node must have a name',
                    )}.`
                  : `${t(
                      'This_name_is_already_taken',
                      'This name is already taken',
                    )}.`
                : `${t(
                    'Node_names_must_be_unique_40_characters_maximum',
                    'Node names must be unique. 40 characters maximum',
                  )}.`}
            </div>
          )}
        </div>
        {/* Email notification implementation, will uncomment the radiogroup once BE is ready */}
        {/* <RadioGroup
          align="horizontal-fixed"
          errorText=""
          name="radio-group-1"
          onChange={(value) => setEmailType(value)}
          value={emailType}
        >
          <RadioButton value="basic" classNames={{wrapper: css.radioButton}}>
            <div className={css.radioBtnInfo}>
              <FormLabelSmall>Basic Email</FormLabelSmall>
              <BodySmall>
                Send simple text-only emails for a straightforward and quick
                communication.
              </BodySmall>
            </div>
          </RadioButton>
          {chatbotAdvanceEmailOptionEnabled && (
            <RadioButton
              value="advanced"
              classNames={{wrapper: css.radioButton}}
            >
              <div className={css.radioBtnInfo}>
                <FormLabelSmall>Advanced Email</FormLabelSmall>
                <BodySmall color="secondary">
                  Create visually engaging emails with our advanced HTML editor
                  for a more impactful message.
                </BodySmall>
              </div>
            </RadioButton>
          )}
        </RadioGroup> */}

        {emailType === 'basic' ? (
          <>
            <EmailInputDetails
              title={`${t('Recipient', 'Recipient')}*`}
              node={node}
              setNode={setNode}
              dynamicLabelMap={dynamicLabelMap}
              tokenOptions={tokenOptions}
              readOnly={isFieldReadOnly('notification_to_recipients')}
              attribute="notification_to_recipients"
              // @ts-expect-error - TS2322 - Type 'Validation | null | undefined' is not assignable to type 'null | undefined'.
              error={error}
            />

            {notificationEnhancementEnabled && (
              <EmailInputDetails
                title={t('cc', 'cc')}
                node={node}
                setNode={setNode}
                dynamicLabelMap={dynamicLabelMap}
                tokenOptions={tokenOptions}
                readOnly={isFieldReadOnly('notification_cc_recipients')}
                attribute="notification_cc_recipients"
                // @ts-expect-error - TS2322 - Type 'Validation | null | undefined' is not assignable to type 'null | undefined'.
                error={error}
              />
            )}
            <EmailSubjectDetails
              placeholder={`${t('Enter_the_subject', 'Enter the subject')}...`}
              defaultValue={node.notification_email_subject}
              // @ts-expect-error - TS7006 - Parameter 'notification_email_subject' implicitly has an 'any' type.
              onChange={(notification_email_subject) => {
                setNode({...node, notification_email_subject});
              }}
              entityType={graph.anchor_entity_type}
              nodeVariables={nodeVariables}
              label=""
              useCase={graph.use_case}
              readOnly={isFieldReadOnly('notification_email_subject')}
              error={error}
              showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
              showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
              warningMessage={
                isUsingDiscoverAttributeInSubject
                  ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                  : ''
              }
            />
            <div className={css.section}>
              <div className={css.inputLabel}>{`${t(
                'Message',
                'Message',
              )}*`}</div>
              {richTextEditorEnabled ? (
                <RichEditorWithWarning
                  warningMessage={
                    isUsingDiscoverAttribute
                      ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                      : ''
                  }
                  defaultValue={node.notification_text_content}
                  onChange={(notification_text_content) => {
                    setNode({...node, notification_text_content});
                  }}
                  editorHeight="200px"
                  includeFormatting={Boolean(richTextEditorFormattingEnabled)}
                  includeHyperlink={!chatbotNewHyperlinkModalEnabled}
                  includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
                  includeGif
                  // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
                  nodeVariables={mappedNodeVariables}
                  atsVariables={
                    graph.use_case === 'sourcing' ? undefined : dynamicLabels
                  }
                  readOnly={isFieldReadOnly('notification_text_content')}
                  entityType={graph.anchor_entity_type}
                  prepareContentOnLoad={prepareContentOnLoad}
                  atsTooltip={
                    hasAncestorJobMatchQuestionNode
                      ? `${t(
                          'ATS_External_variables',
                          'ATS & External variables',
                        )}`
                      : `${t('ATS_variables', 'ATS variables')}`
                  }
                  enableDraftJSONCopy
                />
              ) : (
                <MessageDraftEditor
                  placeholder={`${t(
                    'Enter_a_question',
                    'Enter a question',
                  )}...`}
                  defaultValue={node.notification_text_content}
                  onChange={(notification_text_content) =>
                    setNode({...node, notification_text_content})
                  }
                  entityType={graph.anchor_entity_type}
                  isFirstSmsMessage={false}
                  extraCharacterCount={0}
                  label=""
                  useCase={graph.use_case}
                  nodeVariables={nodeVariables}
                  readOnly={isFieldReadOnly('notification_text_content')}
                  showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
                  showExternalVariables={Boolean(
                    graph.flow_config.api_bot_enabled,
                  )}
                  warningMessage={
                    isUsingDiscoverAttribute
                      ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                      : ''
                  }
                />
              )}
            </div>
          </>
        ) : (
          <>
            <Card classNames={{wrapper: css.cardWrapper}}>
              <CardHeader>
                <CardTitle>{t('Email_Details', 'Email Details')}</CardTitle>
                <CardActions>
                  <Button
                    onClick={() => {
                      handleSaveNode();
                      router.push(
                        `/conversations/${flowId}/email-editor?type=notification&nodeId=${node.id}`,
                      );
                    }}
                    disabled={createAdvancedEmailBtnDisabled}
                    type="secondary"
                  >
                    {/* {email_config.beefree_json &&
                        email_config.beefree_json !== '{}'
                          ? 'View Email'
                          : 'Create Email'} */}
                    {t('Create_Email', 'Create Email')}
                  </Button>
                </CardActions>
              </CardHeader>
              <CardContent className={css.content}>
                <EmailInputDetails
                  title={`${t('Recipient', 'Recipient')}*`}
                  node={node}
                  setNode={setNode}
                  dynamicLabelMap={dynamicLabelMap}
                  tokenOptions={tokenOptions}
                  readOnly={isFieldReadOnly('notification_to_recipients')}
                  attribute="notification_to_recipients"
                  // @ts-expect-error - TS2322 - Type 'Validation | null | undefined' is not assignable to type 'null | undefined'.
                  error={error}
                />

                {notificationEnhancementEnabled && (
                  <EmailInputDetails
                    title={t('cc', 'cc')}
                    node={node}
                    setNode={setNode}
                    dynamicLabelMap={dynamicLabelMap}
                    tokenOptions={tokenOptions}
                    readOnly={isFieldReadOnly('notification_cc_recipients')}
                    attribute="notification_cc_recipients"
                  />
                )}
                <EmailSubjectDetails
                  placeholder={`${t(
                    'Enter_the_subject',
                    'Enter the subject',
                  )}...`}
                  defaultValue={node.notification_email_subject}
                  // @ts-expect-error - TS7006 - Parameter 'notification_email_subject' implicitly has an 'any' type.
                  onChange={(notification_email_subject) => {
                    setNode({...node, notification_email_subject});
                  }}
                  useCase={graph.use_case}
                  entityType={graph.anchor_entity_type}
                  nodeVariables={nodeVariables}
                  label=""
                  readOnly={isFieldReadOnly('notification_email_subject')}
                  error={error}
                  showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
                  showExternalVariables={Boolean(
                    graph.flow_config.api_bot_enabled,
                  )}
                  warningMessage={
                    isUsingDiscoverAttributeInSubject
                      ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                      : ''
                  }
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted && !!validation}
        >
          {canFlowUpdate ? `${t('Update', 'Update')}` : `${t('Save', 'Save')}`}
        </Button>
      </div>
    </div>
  );
}

const RecipientInput = ({
  // @ts-expect-error - TS7031 - Binding element 'title' implicitly has an 'any' type.
  title,
  // @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type.
  node,
  // @ts-expect-error - TS7031 - Binding element 'setNode' implicitly has an 'any' type.
  setNode,
  // @ts-expect-error - TS7031 - Binding element 'dynamicLabelMap' implicitly has an 'any' type.
  dynamicLabelMap,
  // @ts-expect-error - TS7031 - Binding element 'tokenOptions' implicitly has an 'any' type.
  tokenOptions,
  // @ts-expect-error - TS7031 - Binding element 'readOnly' implicitly has an 'any' type.
  readOnly,
  // @ts-expect-error - TS7031 - Binding element 'attribute' implicitly has an 'any' type.
  attribute,
}) => {
  const recipients = node[attribute] || [];

  // @ts-expect-error - TS7031 - Binding element 'value' implicitly has an 'any' type. | TS7031 - Binding element 'type' implicitly has an 'any' type.
  const values = recipients.map(({value, type}) => {
    return type === 'text'
      ? {value, label: `${dynamicLabelMap[value]?.display_name ?? value}`}
      : // @ts-expect-error - TS7006 - Parameter 'option' implicitly has an 'any' type.
        tokenOptions.find((option) => option.value === value);
  });
  return (
    <>
      <div className={css.inputLabel}>{title}</div>
      <TokenListInput
        searchable
        isExpandable
        allowArbitraryValues
        values={values || []}
        // @ts-expect-error - TS2339 - Property 'value' does not exist on type '{}'.
        resolveKey={(v) => String(v?.value)}
        // @ts-expect-error - TS2339 - Property 'label' does not exist on type '{}'.
        resolveLabel={(v) => String(v?.label)}
        options={tokenOptions.filter(Boolean)}
        disabled={readOnly}
        onChange={(tokens) => {
          const newNode = {...node} as const;
          newNode[attribute] = tokens.filter(Boolean).map((token) => {
            // @ts-expect-error - TS18046 - 'token' is of type 'unknown'.
            if (/<(.*)>/g.test(token.label)) {
              return {
                type: 'text',
                // @ts-expect-error - TS18046 - 'token' is of type 'unknown'.
                value: token.value,
              };
              // @ts-expect-error - TS18046 - 'token' is of type 'unknown'.
            } else if (/"(.*)"/g.test(token.label)) {
              return {
                type: 'node',
                // @ts-expect-error - TS18046 - 'token' is of type 'unknown'.
                value: token.value,
              };
            } else {
              return {
                type: 'text',
                // @ts-expect-error - TS18046 - 'token' is of type 'unknown'.
                value: token.value,
              };
            }
          });

          setNode(newNode);
        }}
      />
    </>
  );
};

const EmailInputDetails = ({
  // @ts-expect-error - TS7031 - Binding element 'title' implicitly has an 'any' type.
  title,
  // @ts-expect-error - TS7031 - Binding element 'node' implicitly has an 'any' type.
  node,
  // @ts-expect-error - TS7031 - Binding element 'setNode' implicitly has an 'any' type.
  setNode,
  // @ts-expect-error - TS7031 - Binding element 'dynamicLabelMap' implicitly has an 'any' type.
  dynamicLabelMap,
  // @ts-expect-error - TS7031 - Binding element 'tokenOptions' implicitly has an 'any' type.
  tokenOptions,
  // @ts-expect-error - TS7031 - Binding element 'readOnly' implicitly has an 'any' type.
  readOnly,
  // @ts-expect-error - TS7031 - Binding element 'attribute' implicitly has an 'any' type.
  attribute,
  error = null,
}) => {
  const {t} = useLabelTranslation();
  return (
    <div className={css.section}>
      <RecipientInput
        title={title}
        node={node}
        setNode={setNode}
        dynamicLabelMap={dynamicLabelMap}
        tokenOptions={tokenOptions}
        readOnly={readOnly}
        attribute={attribute}
      />
      {/* @ts-expect-error - TS2339 - Property 'notification_to_recipients' does not exist on type 'never'. */}
      {title !== 'cc' && error?.notification_to_recipients && (
        <div className={css.errorText}>
          {t(
            'Please_select_at_least_one_recipient',
            'Please select at least one recipient',
          )}
        </div>
      )}
    </div>
  );
};

const EmailSubjectDetails = ({
  // @ts-expect-error - TS7031 - Binding element 'placeholder' implicitly has an 'any' type.
  placeholder,
  // @ts-expect-error - TS7031 - Binding element 'label' implicitly has an 'any' type.
  label,
  // @ts-expect-error - TS7031 - Binding element 'defaultValue' implicitly has an 'any' type.
  defaultValue,
  // @ts-expect-error - TS7031 - Binding element 'onChange' implicitly has an 'any' type.
  onChange,
  // @ts-expect-error - TS7031 - Binding element 'entityType' implicitly has an 'any' type.
  entityType,
  // @ts-expect-error - TS7031 - Binding element 'nodeVariables' implicitly has an 'any' type.
  nodeVariables,
  // @ts-expect-error - TS7031 - Binding element 'readOnly' implicitly has an 'any' type.
  readOnly,
  // @ts-expect-error - TS7031 - Binding element 'useCase' implicitly has an 'any' type.
  useCase,
  // @ts-expect-error - TS7031 - Binding element 'error' implicitly has an 'any' type.
  error,
  // @ts-expect-error - TS7031 - Binding element 'showDiscoverAttributes' implicitly has an 'any' type.
  showDiscoverAttributes,
  showExternalVariables = false,
  // @ts-expect-error - TS7031 - Binding element 'warningMessage' implicitly has an 'any' type.
  warningMessage,
}) => {
  const {t} = useLabelTranslation();
  return (
    <div className={css.section}>
      <div className={css.inputLabel}>{`${t('Subject', 'Subject')}*`}</div>
      <MessageDraftEditor
        placeholder={placeholder}
        defaultValue={defaultValue}
        onChange={onChange}
        entityType={entityType}
        isFirstSmsMessage={false}
        extraCharacterCount={0}
        useCase={useCase}
        nodeVariables={nodeVariables}
        label={label}
        readOnly={readOnly}
        showDiscoverAttributes={showDiscoverAttributes}
        showExternalVariables={showExternalVariables}
        warningMessage={warningMessage}
      />
      {error?.notification_email_subject && (
        <div className={css.errorText}>
          {t('Please_fill_in_email_subject', 'Please fill in email subject')}
        </div>
      )}
    </div>
  );
};
